import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { api, fmtDate } from "@/lib/api";
import { SERVICES, STATUSES } from "@/lib/constants";
import { StatusBadge, ContactButtons } from "@/components/common";

const sel = "h-9 rounded-md border border-slate-300 px-2 text-sm bg-white";

export default function OwnerRequests() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [q, setQ] = useState({ status: "", service: "", urgent: "", city: "", date_from: "", date_to: "" });
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/owner/requests", { params: q }).then((r) => setItems(r.data)), [q]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setQ({ ...q, [k]: e.target.value });
  return (
    <div data-testid="owner-requests">
      <div className="flex flex-wrap gap-2 items-center rounded-2xl bg-white border border-slate-200 p-3">
        <select value={q.status} onChange={set("status")} className={sel} data-testid="filter-status"><option value="">{t("o.all_status")}</option>{STATUSES.map((s) => <option key={s} value={s}>{t(`st.${s}`)}</option>)}</select>
        <select value={q.service} onChange={set("service")} className={sel} data-testid="filter-service"><option value="">{t("o.all_services")}</option>{SERVICES.map((s) => <option key={s.key} value={s.key}>{t(`svc.${s.key}`)}</option>)}</select>
        <select value={q.urgent} onChange={set("urgent")} className={sel} data-testid="filter-urgent"><option value="">{t("o.any_urgency")}</option><option value="true">{t("o.urgent_only")}</option></select>
        <Input value={q.city} onChange={set("city")} placeholder={t("o.city")} className="w-36 h-9" data-testid="filter-city" />
        <Input type="date" value={q.date_from} onChange={set("date_from")} className="w-40 h-9" data-testid="filter-date-from" />
        <Input type="date" value={q.date_to} onChange={set("date_to")} className="w-40 h-9" data-testid="filter-date-to" />
        <Button variant="ghost" size="sm" onClick={() => setQ({ status: "", service: "", urgent: "", city: "", date_from: "", date_to: "" })} data-testid="filter-clear">{t("o.clear")}</Button>
      </div>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>{["#", ...["o.h_status", "o.h_service", "o.h_location", "o.h_customer", "o.h_contact", "o.h_pro", "o.h_estimate", "o.h_created"].map((k) => t(k))].map((h) => <th key={h} className="text-start px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">{t("o.no_match")}</td></tr>}
            {items.map((r) => (
              <tr key={r.id} onClick={() => nav(`/owner/requests/${r.id}`)} className="border-t border-slate-100 hover:bg-teal-50/50 cursor-pointer" data-testid={`owner-request-row-${r.number}`}>
                <td className="px-4 py-3 font-mono font-semibold whitespace-nowrap">{r.urgent && <AlertTriangle className="inline h-3.5 w-3.5 text-red-600 me-1" />}{r.number}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3">{t(r.kind === "cleaning" ? "cl.label" : `svc.${r.service}`)}</td>
                <td className="px-4 py-3">{[r.city, r.neighborhood].filter(Boolean).join(", ")}</td>
                <td className="px-4 py-3">{r.contact_name}<div className="text-xs text-slate-400">{r.contact_phone}</div></td>
                <td className="px-4 py-3"><ContactButtons phone={r.contact_phone} compact text={t("o.wa_text", { name: r.contact_name, n: r.number })} /></td>
                <td className="px-4 py-3">{r.contractor_name || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-xs">{r.estimate ? `${r.estimate.total} ${r.estimate.currency} · ${t(`est.${r.estimate.status}`)}` : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
